import {
  FacilityAddress,
  FacilityImage,
  FacilitySummaryResponse,
} from "@repo/shared-types";
import { Feature, Point, GeoJsonProperties, Geometry } from "geojson";
import { MARKER_OBJECT } from "./markers";

export type ClusterPointProperties = {
  id: string;
  name: string;
  address: Partial<FacilityAddress>;
  image?: Partial<FacilityImage> | null;
  categoryId: string;
  cluster: boolean;
};

export type ClusterPoint = Feature<
  Point,
  ClusterPointProperties & { icon: MarkerValues }
>;

type MarkerValues = (typeof MARKER_OBJECT)[keyof typeof MARKER_OBJECT];

export function mapFacilityToClusterPoint(
  facility:
    | FacilitySummaryResponse
    | (FacilitySummaryResponse & { icon: MarkerValues }),
  existingCoordinates: Set<string>
): ClusterPoint {
  const {
    id,
    categoryId,
    coordinates: { longitude, latitude },
  } = facility;

  let uniqueLongitude = longitude;
  let uniqueLatitude = latitude;
  let key = `${uniqueLongitude},${uniqueLatitude}`;

  const radius = 0.0001;
  const numberOfPoints = 12;
  const angleIncrement = (2 * Math.PI) / numberOfPoints;
  let attempts = 0;
  const maxAttempts = 100;

  while (existingCoordinates.has(key) && attempts < maxAttempts) {
    const angle = angleIncrement * (attempts % numberOfPoints);

    uniqueLongitude = longitude + radius * Math.cos(angle);
    uniqueLatitude = latitude + radius * Math.sin(angle);

    key = `${uniqueLongitude},${uniqueLatitude}`;
    attempts++;
  }

  if (attempts === maxAttempts) {
    console.warn(`Max attempts reached for facility ID: ${id}.`);
  }

  existingCoordinates.add(key);

  function getIcon(
    facility:
      | FacilitySummaryResponse
      | (FacilitySummaryResponse & {
          icon: MarkerValues;
        })
  ) {
    if ("icon" in facility) return facility.icon;
    return "markerFallback";
  }

  return {
    type: "Feature",
    id: id,
    properties: {
      id: id,
      name: facility.name,
      address: facility.address,
      image: facility.image,
      icon: getIcon(facility),
      categoryId: categoryId,
      cluster: false,
    },
    geometry: {
      type: "Point",
      coordinates: [uniqueLongitude, uniqueLatitude],
    },
  };
}

export function isClusterPoint(
  feature: Feature<Geometry, GeoJsonProperties>
): feature is ClusterPoint {
  return feature.geometry.type === "Point" && feature.properties !== undefined;
}
